import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adminAuthHeaders, orderPaymentBadge } from './adminCheckoutUtils';
import AdminShippingPartnersSettings from './AdminShippingPartnersSettings';
import AdminShipFromLocationSelect from './AdminShipFromLocationSelect';
import {
  getSavedShipFromId,
  LABELS_PER_A4_PAGE,
  printShippingLabel,
  printShippingLabelsA4Sheet,
  saveShipFromId,
} from './printShippingLabel';
import {
  configToPrintOptions,
  defaultShipFromId,
  mergeShipFromConfig,
  senderForId,
  SHIP_FROM_DEFAULTS,
} from './shipFromLocationsUtils';
import { buildTrackingUrlFromTemplate, partnerById } from './shippingPartners';
import {
  exportOrdersSummaryCsv,
  fetchAndExportOrdersLinesCsv,
  PACKING_SLIPS_PER_A4_PAGE,
  printOrderPackingSlip,
  printPackingSlipsA4,
} from './adminExportUtils';
import { itemsWithFulfillment } from './orderFulfillmentUtils';
import AdminOrderReturnsSection from './AdminOrderReturnsSection';

const STATUSES = ['', 'pending', 'paid', 'processing', 'partially_shipped', 'shipped', 'completed', 'cancelled'];
/** Status values admins may set manually (partially_shipped is set only by recording a shipment). */
const STATUS_EDIT_OPTIONS = STATUSES.filter((s) => s && s !== 'partially_shipped');
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'cancel_requested', label: 'Cancellation requested' },
  ...STATUSES.filter(Boolean).map((s) => ({ value: s, label: s })),
];

function formatMoney(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

/** @param {{ sent?: boolean, reason?: string, to?: string } | null | undefined} result */
function shippedEmailStatusMessage(result) {
  if (!result || typeof result.reason !== 'string') return '';
  switch (result.reason) {
    case 'sent':
      return result.to ? `Shipped email sent to ${result.to}.` : 'Shipped email sent.';
    case 'disabled':
      return 'Shipped emails are off — turn on in Site → Email → Order notifications.';
    case 'migration':
      return 'Shipped emails need the database migration (migrate_catalog_all.sql on your server).';
    case 'already_sent':
      return 'Shipped email was already sent for this order.';
    case 'no_email':
      return 'No customer email on file — shipped email was not sent.';
    case 'no_tracking':
      return 'Tracking not saved in the database — click Save tracking, then try again.';
    case 'smtp_failed':
      return 'Could not send shipped email — check Site → Email (SMTP + Send test).';
    case 'shipment_emails':
      return 'Customer was notified per shipment — no duplicate whole-order email.';
    default:
      return '';
  }
}

function shipmentEmailStatusMessage(result) {
  if (!result || typeof result.reason !== 'string') return '';
  switch (result.reason) {
    case 'sent':
      return result.to ? `Shipment email sent to ${result.to}.` : 'Shipment email sent.';
    case 'disabled':
      return 'Shipment emails are off — turn on in Site → Email → Order notifications.';
    case 'no_email':
      return 'No customer email — shipment recorded but not emailed.';
    case 'no_tracking':
      return 'Tracking required to email the customer.';
    case 'smtp_failed':
      return 'Could not send shipment email — check Site → Email.';
    default:
      return '';
  }
}

function AdminOrderIcon({ name, className = '' }) {
  const cn = `admin-order-icon ${className}`.trim();
  if (name === 'label') {
    return (
      <svg className={cn} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 6h12l4 4v10H4V6z" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <path d="M16 6v4h4" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <path d="M7 13h6M7 16h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === 'slip') {
    return (
      <svg className={cn} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 4h10a2 2 0 012 2v14l-4-2-4 2-4-2-4 2V6a2 2 0 012-2z" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === 'copy') {
    return (
      <svg className={cn} viewBox="0 0 24 24" aria-hidden="true">
        <rect x="8" y="8" width="11" height="13" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <path d="M6 16H5a2 2 0 01-2-2V5a2 2 0 012-2h9a2 2 0 012 2v1" fill="none" stroke="currentColor" strokeWidth="1.75" />
      </svg>
    );
  }
  if (name === 'mail') {
    return (
      <svg className={cn} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 6h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
        />
        <path d="M4 8l8 5 8-5" fill="none" stroke="currentColor" strokeWidth="1.75" />
      </svg>
    );
  }
  if (name === 'package') {
    return (
      <svg className={cn} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3l8 4.5v9L12 21 4 16.5v-9L12 3z" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" stroke="currentColor" strokeWidth="1.75" />
      </svg>
    );
  }
  return (
    <svg className={cn} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function orderReadyToShip(order) {
  return String(order?.status || '').toLowerCase() === 'shipped';
}

function orderCustomerInitial(order) {
  const name = order?.customer?.display_name || order?.customer?.username || '?';
  const ch = String(name).trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

function copyShippingBlock(shipping) {
  if (!shipping) return '';
  return [
    shipping.name,
    shipping.phone,
    shipping.address,
    [shipping.city, shipping.state, shipping.pincode].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join('\n');
}

export default function AdminOrdersPanel({ sessionToken, setStatus }) {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [statusDraft, setStatusDraft] = useState('pending');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [shippingPartners, setShippingPartners] = useState([]);
  const trackingUrlTouchedRef = useRef(false);
  const [savingTracking, setSavingTracking] = useState(false);
  const [shipmentPick, setShipmentPick] = useState({});
  const [savingShipment, setSavingShipment] = useState(false);
  const [resendingShipmentSeq, setResendingShipmentSeq] = useState(null);
  const [shipFromConfig, setShipFromConfig] = useState(() => mergeShipFromConfig(null));
  const [printOptions, setPrintOptions] = useState(() => configToPrintOptions(SHIP_FROM_DEFAULTS));
  const [shipFromId, setShipFromId] = useState(() => getSavedShipFromId(configToPrintOptions(SHIP_FROM_DEFAULTS)));
  const [exportingLines, setExportingLines] = useState(false);
  const [labelSelected, setLabelSelected] = useState(() => new Set());
  const [printingLabelsBatch, setPrintingLabelsBatch] = useState(false);
  const [printingSlipsBatch, setPrintingSlipsBatch] = useState(false);
  const [sendingCustomerEmail, setSendingCustomerEmail] = useState(false);

  const loadShipFromLocations = useCallback(async () => {
    try {
      const r = await fetch('/api/admin_ship_from_locations.php', { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (r.ok && d.success) {
        const cfg = mergeShipFromConfig(d.config);
        const opts = Array.isArray(d.print_options) && d.print_options.length
          ? d.print_options
          : configToPrintOptions(cfg);
        setShipFromConfig(cfg);
        setPrintOptions(opts);
        const def = defaultShipFromId(cfg, opts);
        const saved = getSavedShipFromId(opts);
        setShipFromId(opts.some((o) => o.id === saved) ? saved : def);
      }
    } catch {
      /* use defaults */
    }
  }, [sessionToken]);

  const loadShippingPartners = useCallback(async () => {
    try {
      const r = await fetch('/api/admin_shipping_partners.php', { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (r.ok && d.success && Array.isArray(d.partners)) {
        setShippingPartners(d.partners);
      }
    } catch {
      /* optional */
    }
  }, [sessionToken]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (customerFilter) params.set('customer_id', customerFilter);
      if (search.trim()) params.set('q', search.trim());
      params.set('limit', '100');
      const qs = params.toString();
      const r = await fetch(`/api/admin_orders.php${qs ? `?${qs}` : ''}`, {
        headers: adminAuthHeaders(sessionToken),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not load orders.');
        setOrders([]);
        return;
      }
      setOrders(Array.isArray(d.orders) ? d.orders : []);
      if (d.auto_completed_count > 0) {
        setStatus(
          `${d.auto_completed_count} shipped order${d.auto_completed_count === 1 ? '' : 's'} marked completed (auto).`
        );
      }
    } catch {
      setError('Could not load orders.');
      setOrders([]);
    } finally {
      setLoadingList(false);
    }
  }, [sessionToken, statusFilter, customerFilter, search]);

  const loadCustomers = useCallback(async () => {
    try {
      const r = await fetch('/api/admin_orders.php?customers=1', {
        headers: adminAuthHeaders(sessionToken),
      });
      const d = await r.json();
      if (r.ok && d.success && Array.isArray(d.customers)) {
        setCustomers(d.customers);
      }
    } catch {
      /* optional */
    }
  }, [sessionToken]);

  const customerFilterOptions = useMemo(() => {
    const withOrders = customers.filter((c) => (c.order_count || 0) > 0);
    const list = withOrders.length ? withOrders : customers;
    return list
      .slice()
      .sort((a, b) => {
        const ac = a.order_count || 0;
        const bc = b.order_count || 0;
        if (bc !== ac) return bc - ac;
        const an = (a.display_name || a.username || '').toLowerCase();
        const bn = (b.display_name || b.username || '').toLowerCase();
        return an.localeCompare(bn);
      })
      .map((c) => ({
        id: String(c.id),
        label: [c.display_name || c.username, c.email].filter(Boolean).join(' — '),
        orderCount: c.order_count || 0,
      }));
  }, [customers]);

  const displayItems = useMemo(() => detail?.items || [], [detail]);

  const applyTrackingFieldsFromOrder = useCallback((order) => {
    const shipments = order?.shipments || [];
    const enriched = itemsWithFulfillment(order?.items, shipments);
    const hasRemaining = enriched.some((it) => (it.quantity_remaining ?? 0) > 0);
    if (shipments.length > 0 && hasRemaining) {
      setTrackingNumber('');
      setTrackingUrl('');
      setTrackingCarrier('');
      trackingUrlTouchedRef.current = false;
      return;
    }
    const latest = shipments.length ? shipments[shipments.length - 1] : null;
    setTrackingNumber(latest?.tracking_number || order?.tracking_number || '');
    setTrackingUrl(latest?.tracking_url || order?.tracking_url || '');
    setTrackingCarrier(latest?.tracking_carrier || order?.tracking_carrier || '');
    trackingUrlTouchedRef.current = false;
  }, []);

  const applyOrderDetail = useCallback(
    (order) => {
      if (!order) {
        setDetail(null);
        return;
      }
      const enriched = {
        ...order,
        items: itemsWithFulfillment(order.items, order.shipments),
      };
      setDetail(enriched);
      setStatusDraft(String(enriched.status || 'pending'));
      applyTrackingFieldsFromOrder(enriched);
    },
    [applyTrackingFieldsFromOrder]
  );

  const loadDetail = useCallback(
    async (orderNumber) => {
      if (!orderNumber) {
        setDetail(null);
        return;
      }
      setLoadingDetail(true);
      setError('');
      try {
        const r = await fetch(`/api/admin_orders.php?order_number=${encodeURIComponent(orderNumber)}`, {
          headers: adminAuthHeaders(sessionToken),
        });
        const d = await r.json();
        if (!r.ok || !d.success) {
          setError(d.error || 'Could not load order.');
          setDetail(null);
          return;
        }
        applyOrderDetail(d.order);
      } catch {
        setError('Could not load order.');
        setDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [sessionToken, applyOrderDetail]
  );

  const selectOrder = useCallback(
    (orderNumber) => {
      if (!orderNumber) return;
      if (orderNumber === selected) {
        loadDetail(orderNumber);
      } else {
        setSelected(orderNumber);
      }
    },
    [selected, loadDetail]
  );

  useEffect(() => {
    loadList();
    loadShippingPartners();
    loadShipFromLocations();
    loadCustomers();
  }, [loadList, loadShippingPartners, loadShipFromLocations, loadCustomers]);

  useEffect(() => {
    const shippable = new Set(orders.filter(orderReadyToShip).map((o) => o.order_number));
    setLabelSelected((prev) => {
      const next = new Set([...prev].filter((n) => shippable.has(n)));
      return next.size === prev.size ? prev : next;
    });
  }, [orders]);

  useEffect(() => {
    if (trackingUrlTouchedRef.current) return;
    const partner = partnerById(shippingPartners, trackingCarrier);
    if (!partner?.url_template || !String(trackingNumber || '').trim()) return;
    const built = buildTrackingUrlFromTemplate(partner.url_template, trackingNumber);
    if (built) setTrackingUrl(built);
  }, [trackingCarrier, trackingNumber, shippingPartners]);

  useEffect(() => {
    setShipmentPick({});
  }, [selected, detail?.order_number]);

  useEffect(() => {
    if (selected) loadDetail(selected);
    else setDetail(null);
  }, [selected, loadDetail]);

  const detailBadge = useMemo(() => (detail ? orderPaymentBadge(detail) : null), [detail]);

  const labelSelectedCount = labelSelected.size;

  const toggleLabelSelect = (orderNumber, checked) => {
    setLabelSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(orderNumber);
      else next.delete(orderNumber);
      return next;
    });
  };

  const selectAllForLabels = () => {
    setLabelSelected(new Set(orders.filter(orderReadyToShip).map((o) => o.order_number)));
  };

  const shippedSelectedCount = useMemo(
    () => orders.filter((o) => labelSelected.has(o.order_number) && orderReadyToShip(o)).length,
    [orders, labelSelected]
  );

  const clearLabelSelection = () => setLabelSelected(new Set());

  const fetchOrderDetailsForBatch = useCallback(
    async (orderNumbers) => {
      const results = await Promise.all(
        orderNumbers.map(async (num) => {
          try {
            const r = await fetch(`/api/admin_orders.php?order_number=${encodeURIComponent(num)}`, {
              headers: adminAuthHeaders(sessionToken),
            });
            const d = await r.json();
            if (r.ok && d.success && d.order) return d.order;
          } catch {
            /* skip */
          }
          return null;
        })
      );
      return {
        orders: results.filter(Boolean),
        skipped: orderNumbers.length - results.filter(Boolean).length,
      };
    },
    [sessionToken]
  );

  const printSelectedLabelsOnA4 = async () => {
    const nums = [...labelSelected];
    if (!nums.length) return;
    if (nums.length > 60) {
      window.alert('Select at most 60 orders at a time for label printing.');
      return;
    }
    setPrintingLabelsBatch(true);
    try {
      const { orders: loaded, skipped } = await fetchOrderDetailsForBatch(nums);
      const ok = loaded.filter(orderReadyToShip);
      const notShipped = loaded.length - ok.length + (nums.length - loaded.length);
      if (!ok.length) {
        window.alert(
          notShipped > 0
            ? 'Labels print only for orders with status Shipped. Update status first, then try again.'
            : 'Could not load orders for printing.'
        );
        return;
      }
      const sender = senderForId(printOptions, shipFromId);
      const printed = await printShippingLabelsA4Sheet(ok, sender);
      if (printed) {
        const pages = Math.ceil(ok.length / LABELS_PER_A4_PAGE);
        setStatus(
          `Printing ${ok.length} shipping label${ok.length === 1 ? '' : 's'} on ${pages} A4 page${pages === 1 ? '' : 's'} (${LABELS_PER_A4_PAGE} per page).${
            notShipped ? ` ${notShipped} not shipped — skipped.` : ''
          }`
        );
      }
    } finally {
      setPrintingLabelsBatch(false);
    }
  };

  const printSelectedSlipsOnA4 = async () => {
    const nums = [...labelSelected];
    if (!nums.length) return;
    if (nums.length > 60) {
      window.alert('Select at most 60 orders at a time for packing slip printing.');
      return;
    }
    setPrintingSlipsBatch(true);
    try {
      const { orders: loaded, skipped } = await fetchOrderDetailsForBatch(nums);
      const ok = loaded.filter(orderReadyToShip);
      const notShipped = loaded.length - ok.length + (nums.length - loaded.length);
      if (!ok.length) {
        window.alert(
          notShipped > 0
            ? 'Packing slips print only for orders with status Shipped. Update status first, then try again.'
            : 'Could not load orders for printing.'
        );
        return;
      }
      const printed = await printPackingSlipsA4(ok);
      if (printed) {
        setStatus(
          `Printing ${ok.length} packing slip${ok.length === 1 ? '' : 's'} (up to ${PACKING_SLIPS_PER_A4_PAGE} per A4 page when the order is small).${
            notShipped ? ` ${notShipped} not shipped — skipped.` : ''
          }`
        );
      }
    } finally {
      setPrintingSlipsBatch(false);
    }
  };

  const trackingDraftDirty = useCallback(() => {
    if (!detail) {
      return Boolean(trackingNumber.trim() || trackingUrl.trim() || trackingCarrier.trim());
    }
    return (
      (trackingNumber.trim() || '') !== (detail.tracking_number || '').trim() ||
      (trackingUrl.trim() || '') !== (detail.tracking_url || '').trim() ||
      (trackingCarrier.trim() || '') !== (detail.tracking_carrier || '').trim()
    );
  }, [detail, trackingNumber, trackingUrl, trackingCarrier]);

  const hasTrackingDraft = useCallback(
    () => Boolean(trackingNumber.trim()) || Boolean(trackingUrl.trim()),
    [trackingNumber, trackingUrl]
  );

  const persistTracking = async () => {
    if (!selected) return { ok: false };
    const r = await fetch('/api/admin_orders.php', {
      method: 'POST',
      headers: adminAuthHeaders(sessionToken),
      body: JSON.stringify({
        action: 'update_tracking',
        order_number: selected,
        tracking_number: trackingNumber.trim(),
        tracking_url: trackingUrl.trim(),
        tracking_carrier: trackingCarrier.trim(),
      }),
    });
    const d = await r.json();
    if (!r.ok || !d.success) {
      setError(d.error || 'Could not save tracking.');
      return { ok: false };
    }
    await loadDetail(selected);
    return { ok: true, shipped_email: d.shipped_email };
  };

  const saveStatus = async () => {
    if (!selected) return;
    if (statusDraft === 'cancelled' && detail?.status !== 'cancelled') {
      const hadStock =
        detail?.status &&
        ['paid', 'processing', 'partially_shipped', 'shipped', 'completed'].includes(detail.status);
      const offlinePending =
        detail?.status === 'pending' && !detail?.razorpay_order_id;
      let msg = 'Cancel this order?';
      if (hadStock || offlinePending) {
        msg += ' Stock for this order will be returned to the shop.';
      }
      if (!window.confirm(msg)) return;
    }
    setSavingStatus(true);
    setError('');
    try {
      if (statusDraft === 'shipped' && trackingDraftDirty() && hasTrackingDraft()) {
        setSavingTracking(true);
        const tr = await persistTracking();
        setSavingTracking(false);
        if (!tr.ok) return;
      }
      const r = await fetch('/api/admin_orders.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          action: 'update_status',
          order_number: selected,
          status: statusDraft,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not update status.');
        return;
      }
      const emailNote = shippedEmailStatusMessage(d.shipped_email);
      setStatus(
        emailNote
          ? `Order ${selected} marked as ${statusDraft}. ${emailNote}`
          : `Order ${selected} marked as ${statusDraft}.`
      );
      await loadList();
      await loadDetail(selected);
    } catch {
      setError('Could not update status.');
    } finally {
      setSavingStatus(false);
      setSavingTracking(false);
    }
  };

  const saveTracking = async () => {
    if (!selected) return;
    setSavingTracking(true);
    setError('');
    try {
      const tr = await persistTracking();
      if (!tr.ok) return;
      const emailNote = shippedEmailStatusMessage(tr.shipped_email);
      setStatus(
        emailNote
          ? `Tracking saved for order ${selected}. ${emailNote}`
          : `Tracking saved for order ${selected}.`
      );
    } catch {
      setError('Could not save tracking.');
    } finally {
      setSavingTracking(false);
    }
  };

  const markPaid = async () => {
    if (!selected) return;
    if (
      !window.confirm(
        'Mark this order as paid? Stock will be deducted if it was not already reserved (e.g. bank transfer received).'
      )
    ) {
      return;
    }
    setMarkingPaid(true);
    setError('');
    try {
      const r = await fetch('/api/admin_orders.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ action: 'mark_paid', order_number: selected }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not mark as paid.');
        return;
      }
      setStatus(`Order ${selected} marked as paid.`);
      await loadList();
      await loadDetail(selected);
    } catch {
      setError('Could not mark as paid.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const sendCustomerEmail = async () => {
    if (!selected || !detail) return;
    setSendingCustomerEmail(true);
    setError('');
    try {
      const r = await fetch('/api/admin_orders.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ action: 'send_customer_email', order_number: selected }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not send email.');
        return;
      }
      setStatus(d.message || `Email sent to ${d.sent_to}.`);
    } catch {
      setError('Could not send email.');
    } finally {
      setSendingCustomerEmail(false);
    }
  };

  const customerEmailHint = useMemo(() => {
    if (!detail) return '';
    const hasTracking =
      Boolean(detail.tracking_number?.trim()) || Boolean(detail.tracking_url?.trim());
    const shipped = ['shipped', 'processing', 'partially_shipped'].includes(detail.status);
    if (shipped && hasTracking) {
      return 'Sends order lines, totals, address, and courier tracking (use if they deleted the shipped email).';
    }
    return 'Sends order lines, totals, delivery address, and payment status.';
  }, [detail]);

  const orderHasRemainingItems = useMemo(() => {
    if (!detail?.partial_shipments_enabled) return false;
    return displayItems.some((it) => (it.quantity_remaining ?? 0) > 0);
  }, [detail, displayItems]);

  const partialShipmentBlockReason = useMemo(() => {
    if (!detail?.partial_shipments_enabled) {
      return 'Run database/migrate_catalog_all.sql on your server to enable partial shipments.';
    }
    if (!orderHasRemainingItems) {
      return 'All items on this order are already marked as shipped.';
    }
    const st = String(detail.status || '').toLowerCase();
    if (st === 'cancelled' || st === 'canceled') {
      return 'Cancelled orders cannot be shipped.';
    }
    if (st === 'pending' && !detail.paid_at) {
      return 'Mark the order as paid first (or use Mark as paid), then you can ship items.';
    }
    return '';
  }, [detail, orderHasRemainingItems]);

  const canRecordPartialShipment = useMemo(
    () => detail?.partial_shipments_enabled && orderHasRemainingItems && partialShipmentBlockReason === '',
    [detail, orderHasRemainingItems, partialShipmentBlockReason]
  );

  const recordShipment = async () => {
    if (!selected || !detail) return;
    const lineItems = Object.entries(shipmentPick)
      .map(([orderItemId, quantity]) => ({
        order_item_id: Number(orderItemId),
        quantity: Number(quantity),
      }))
      .filter((row) => row.order_item_id > 0 && row.quantity > 0);
    if (!canRecordPartialShipment) {
      setError(partialShipmentBlockReason || 'Cannot record a shipment for this order.');
      return;
    }
    if (!lineItems.length) {
      setError('Select at least one line to include in this shipment.');
      return;
    }
    if (!trackingNumber.trim() && !trackingUrl.trim()) {
      setError('Enter tracking (AWB) or a tracking link for this shipment.');
      return;
    }
    setSavingShipment(true);
    setError('');
    try {
      const r = await fetch('/api/admin_orders.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          action: 'create_shipment',
          order_number: selected,
          line_items: lineItems,
          tracking_number: trackingNumber.trim(),
          tracking_url: trackingUrl.trim(),
          tracking_carrier: trackingCarrier.trim(),
          send_email: true,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not record shipment.');
        return;
      }
      const emailNote = shipmentEmailStatusMessage(d.shipment_email);
      setStatus(
        emailNote
          ? `Shipment ${d.shipment_seq} recorded (${d.status}). ${emailNote}`
          : `Shipment ${d.shipment_seq} recorded — order is now ${d.status}.`
      );
      setShipmentPick({});
      if (d.order) {
        applyOrderDetail(d.order);
      } else {
        await loadDetail(selected);
      }
      await loadList();
    } catch {
      setError('Could not record shipment.');
    } finally {
      setSavingShipment(false);
    }
  };

  const resendShipmentEmail = async (shipmentSeq) => {
    if (!selected || !shipmentSeq) return;
    setResendingShipmentSeq(shipmentSeq);
    setError('');
    try {
      const r = await fetch('/api/admin_orders.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          action: 'resend_shipment_email',
          order_number: selected,
          shipment_seq: shipmentSeq,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not resend email.');
        return;
      }
      const emailNote = shipmentEmailStatusMessage(d.shipment_email);
      setStatus(emailNote || `Shipment ${shipmentSeq} email sent.`);
      if (d.order) applyOrderDetail(d.order);
      else await loadDetail(selected);
    } catch {
      setError('Could not resend shipment email.');
    } finally {
      setResendingShipmentSeq(null);
    }
  };

  const shippedNotificationHint = useMemo(() => {
    if (!detail) return '';
    if (detail.partial_shipments_enabled && (detail.shipments || []).length > 0) {
      return 'Each recorded shipment can email the customer with tracking and only the items in that package. Remaining items stay on the order until you ship them.';
    }
    if (detail.partial_shipments_enabled && canRecordPartialShipment) {
      return 'Ship available items now (partial shipment), then ship the rest when ready — each parcel gets its own tracking email.';
    }
    if (detail.status === 'completed') {
      const when = detail.completed_at || detail.shipped_at;
      return when
        ? `Order completed${detail.shipped_at ? ` (shipped ${detail.shipped_at})` : ''}.`
        : 'Order completed — archived after the auto-complete period.';
    }
    if (detail.shipped_notification_sent_at) {
      return `Automatic shipped email sent on ${detail.shipped_notification_sent_at}.`;
    }
    if (detail.status !== 'shipped') {
      return 'When you mark Shipped (after Save tracking), customers get one automatic email with tracking — if enabled in Site → Email.';
    }
    const hasTracking =
      Boolean(detail.tracking_number?.trim()) || Boolean(detail.tracking_url?.trim());
    if (!detail.customer?.email) {
      return 'Shipped — no customer email; automatic shipped email cannot send.';
    }
    if (!hasTracking) {
      return 'Shipped — save tracking number or link, then click Save tracking to send the automatic shipped email.';
    }
    return 'Shipped with tracking saved — if no email arrived, check Site → Email (SMTP) or use Resend order email.';
  }, [detail, canRecordPartialShipment]);

  const copyAddress = async () => {
    const text = copyShippingBlock(detail?.shipping);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setStatus('Customer delivery address copied to clipboard.');
    } catch {
      setStatus('Could not copy — select the text under Delivery address and copy manually.');
    }
  };

  const canMarkPaid =
    detail &&
    !['paid', 'processing', 'shipped', 'completed', 'cancelled'].includes(detail.status);

  const canPrintShipDocs = Boolean(detail && orderReadyToShip(detail));

  return (
    <div className="admin-orders">
      <AdminShippingPartnersSettings sessionToken={sessionToken} setStatus={setStatus} onSaved={loadShippingPartners} />
      <div className="admin-orders-page-head card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
            <div>
              <h2 className="admin-orders-page-head__title h5 mb-1">Customer orders</h2>
              <p className="small text-muted mb-0">
                Filter, search, and fulfill orders. Cancelling restores stock when it was allocated.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 admin-orders-page-head__actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={!orders.length}
                onClick={() =>
                  exportOrdersSummaryCsv(orders, {
                    statusLabel: statusFilter || 'all',
                    searchLabel: search.trim() || '',
                    customerLabel:
                      customerFilterOptions.find((c) => c.id === customerFilter)?.label || '',
                  })
                }
                title="Current list — opens in Excel"
              >
                Export CSV
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={exportingLines}
                onClick={async () => {
                  setExportingLines(true);
                  try {
                    await fetchAndExportOrdersLinesCsv(sessionToken, {
                      statusFilter,
                      customerFilter,
                      search,
                      adminAuthHeaders,
                    });
                  } finally {
                    setExportingLines(false);
                  }
                }}
                title="Orders with line items (up to 200)"
              >
                {exportingLines ? 'Exporting…' : 'Export with lines'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  loadList();
                  if (selected) loadDetail(selected);
                }}
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="admin-orders-filters">
            <div className="admin-orders-filters__field">
              <label className="form-label small mb-1" htmlFor="admin-orders-status">
                Status
              </label>
              <select
                id="admin-orders-status"
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-orders-filters__field admin-orders-filters__customer">
              <label className="form-label small mb-1" htmlFor="admin-orders-customer">
                Customer
              </label>
              <select
                id="admin-orders-customer"
                className="form-select form-select-sm"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
              >
                <option value="">All customers</option>
                {customerFilterOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                    {opt.orderCount > 0 ? ` (${opt.orderCount})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-orders-filters__search">
              <label className="form-label small mb-1" htmlFor="admin-orders-search">
                Search
              </label>
              <div className="input-group input-group-sm">
                <input
                  id="admin-orders-search"
                  type="search"
                  className="form-control"
                  placeholder="Order #, name, phone, city…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSearch(searchDraft);
                  }}
                />
                <button type="button" className="btn btn-primary" onClick={() => setSearch(searchDraft)}>
                  Search
                </button>
              </div>
            </div>
          </div>

          {error ? <div className="alert alert-danger py-2 small mb-0">{error}</div> : null}
        </div>
      </div>

      <div className="row g-3 align-items-start">
        <div className="col-lg-5">
          <div className="card border-0 admin-orders-list-card">
            <div className="admin-orders-list-card__head">
              <div className="admin-orders-list-card__head-main">
                <span className="admin-orders-list-card__kicker">Fulfillment inbox</span>
                <h3 className="admin-orders-list-card__title h6 mb-0">
                  Orders
                  {orders.length > 0 ? (
                    <span className="admin-orders-list-card__count">{orders.length}</span>
                  ) : null}
                </h3>
              </div>
              {orders.length > 0 ? (
                <p className="admin-orders-list-card__hint small mb-0">
                  Tap a row for details · batch print: <strong>Shipped</strong> only
                </p>
              ) : null}
            </div>

            <div className="admin-orders-list-card__scroll">
              {loadingList ? (
                <div className="admin-orders-list-card__state">
                  <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                  <span className="small text-muted">Loading orders…</span>
                </div>
              ) : orders.length === 0 ? (
                <div className="admin-orders-list-card__state">
                  <AdminOrderIcon name="package" className="admin-orders-list-card__state-icon" />
                  <span className="small text-muted">No orders match your filters.</span>
                </div>
              ) : (
                <ul className="admin-orders-list mb-0">
                  {orders.map((o) => {
                    const payBadge = orderPaymentBadge(o);
                    const isActive = selected === o.order_number;
                    const isChecked = labelSelected.has(o.order_number);
                    return (
                      <li
                        key={o.order_number}
                        className={`admin-order-row${isActive ? ' admin-order-row--active' : ''}${
                          isChecked ? ' admin-order-row--checked' : ''
                        }`}
                      >
                        <button
                          type="button"
                          className="admin-order-row__open"
                          onClick={() => selectOrder(o.order_number)}
                        >
                          <span className="admin-order-row__avatar" aria-hidden="true">
                            {orderCustomerInitial(o)}
                          </span>
                          <span className="admin-order-row__content">
                            <span className="admin-order-row__line1">
                              <span className="admin-order-row__id" title={`Order ${o.order_number}`}>
                                #{o.order_number}
                              </span>
                              <span className="admin-order-row__amount">{formatMoney(o.total_amount)}</span>
                            </span>
                            <span className="admin-order-row__line2">
                              {o.customer?.display_name || o.customer?.username || 'Customer'}
                              {o.shipping_city ? (
                                <span className="admin-order-row__city"> · {o.shipping_city}</span>
                              ) : null}
                            </span>
                            <span className="admin-order-row__line3">
                              <span
                                className={`admin-order-row__status admin-order-row__status--${o.status}`}
                              >
                                {o.status}
                              </span>
                              <span className="admin-order-row__pay">{payBadge.label}</span>
                              {o.cancel_requested_at ? (
                                <span className="admin-order-row__flag">Cancel requested</span>
                              ) : null}
                            </span>
                          </span>
                        </button>
                        <label
                          className={`admin-order-row__check${
                            !orderReadyToShip(o) ? ' admin-order-row__check--off' : ''
                          }`}
                          title={
                            orderReadyToShip(o)
                              ? 'Select for batch print'
                              : 'Set status to Shipped before printing labels or slips'
                          }
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isChecked}
                            disabled={!orderReadyToShip(o)}
                            onChange={(e) => toggleLabelSelect(o.order_number, e.target.checked)}
                            aria-label={`Select order ${o.order_number}`}
                          />
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {orders.length > 0 ? (
              <div className="admin-orders-list-card__foot">
                <div className="admin-orders-list-card__foot-head">
                  <span className="admin-orders-list-card__foot-title">Batch print</span>
                  <span className="admin-orders-list-card__foot-actions">
                    <span
                      className={`admin-orders-list-card__sel${
                        labelSelectedCount ? ' admin-orders-list-card__sel--on' : ''
                      }`}
                    >
                      {labelSelectedCount} selected
                    </span>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={selectAllForLabels}>
                      All
                    </button>
                    <span className="text-muted" aria-hidden="true">
                      ·
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      disabled={!labelSelectedCount}
                      onClick={clearLabelSelection}
                    >
                      Clear
                    </button>
                  </span>
                </div>
                <div className="admin-orders-print-actions">
                  <div className="admin-orders-print-action">
                    <span className="admin-orders-print-action__icon">
                      <AdminOrderIcon name="label" />
                    </span>
                    <span className="admin-orders-print-action__info">
                      <span className="admin-orders-print-action__label">Shipping labels</span>
                      <span className="admin-orders-print-action__meta">{LABELS_PER_A4_PAGE} per A4 sheet</span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={
                        !shippedSelectedCount || printingLabelsBatch || printingSlipsBatch
                      }
                      title={
                        shippedSelectedCount
                          ? `Print labels for ${shippedSelectedCount} shipped order(s)`
                          : 'Select shipped orders only — set status to Shipped first'
                      }
                      onClick={printSelectedLabelsOnA4}
                    >
                      {printingLabelsBatch ? '…' : 'Print labels'}
                    </button>
                  </div>
                  <div className="admin-orders-print-action">
                    <span className="admin-orders-print-action__icon">
                      <AdminOrderIcon name="slip" />
                    </span>
                    <span className="admin-orders-print-action__info">
                      <span className="admin-orders-print-action__label">Packing slips</span>
                      <span className="admin-orders-print-action__meta">{PACKING_SLIPS_PER_A4_PAGE} per A4 sheet</span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={
                        !shippedSelectedCount || printingSlipsBatch || printingLabelsBatch
                      }
                      title={
                        shippedSelectedCount
                          ? `Print slips for ${shippedSelectedCount} shipped order(s)`
                          : 'Select shipped orders only — set status to Shipped first'
                      }
                      onClick={printSelectedSlipsOnA4}
                    >
                      {printingSlipsBatch ? '…' : 'Print slips'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card border-0 shadow-sm admin-orders-detail-card">
            <div className="card-body p-0">
              {!selected ? (
                <div className="admin-orders-detail-empty">
                  <AdminOrderIcon name="package" className="admin-orders-detail-empty__icon" />
                  <p className="admin-orders-detail-empty__title mb-1">Select an order</p>
                  <p className="small text-muted mb-0">Choose an order on the left to view items, print labels, and update tracking.</p>
                </div>
              ) : loadingDetail || !detail ? (
                <div className="admin-orders-detail-empty">
                  <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                  <p className="small text-muted mb-0 mt-2">{loadingDetail ? 'Loading order…' : '—'}</p>
                </div>
              ) : (
                <div className="admin-orders-detail">
                  <div className="admin-orders-detail__hero">
                    <div className="admin-orders-detail__hero-main">
                      <span className="admin-orders-detail__eyebrow">Order</span>
                      <h3 className="admin-orders-detail__number mb-1">#{detail.order_number}</h3>
                      <p className="admin-orders-detail__meta small mb-2">
                        Placed {detail.created_at ? new Date(detail.created_at).toLocaleString('en-IN') : '—'}
                        {detail.paid_at ? ` · Paid ${new Date(detail.paid_at).toLocaleString('en-IN')}` : ''}
                      </p>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        <span
                          className={`admin-order-row__status admin-order-row__status--${detail.status}`}
                        >
                          {detail.status}
                        </span>
                        {detailBadge ? (
                          <span className={`badge ${detailBadge.className}`}>{detailBadge.label}</span>
                        ) : null}
                      </div>
                      {(detail.payment_method ||
                        detail.razorpay_order_id ||
                        detail.razorpay_payment_id) ? (
                        <dl className="admin-orders-payment-ids small mb-0 mt-2">
                          {detail.payment_method ? (
                            <>
                              <dt className="text-muted">Payment method</dt>
                              <dd className="mb-1 text-capitalize">{detail.payment_method}</dd>
                            </>
                          ) : null}
                          {detail.payment_instrument ? (
                            <>
                              <dt className="text-muted">Paid with</dt>
                              <dd className="mb-1">{detail.payment_instrument}</dd>
                            </>
                          ) : null}
                          {detail.razorpay_order_id ? (
                            <>
                              <dt className="text-muted">Razorpay order</dt>
                              <dd className="mb-1">
                                <code className="small">{detail.razorpay_order_id}</code>
                              </dd>
                            </>
                          ) : null}
                          {detail.razorpay_payment_id ? (
                            <>
                              <dt className="text-muted">Razorpay payment</dt>
                              <dd className="mb-0">
                                <code className="small">{detail.razorpay_payment_id}</code>
                              </dd>
                            </>
                          ) : null}
                        </dl>
                      ) : null}
                    </div>
                    <div className="admin-orders-detail__hero-total">
                      <span className="admin-orders-detail__total-label">Total</span>
                      <span className="admin-orders-detail__total-value">{formatMoney(detail.total_amount)}</span>
                      <span className="admin-orders-detail__total-sub">
                        {formatMoney(detail.subtotal)} + {formatMoney(detail.shipping_amount)} ship
                        {detail.shipping_zone ? ` (${detail.shipping_zone})` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="admin-orders-detail__body">

                  {detail.cancel_requested_at ? (
                    <div className="alert alert-warning py-2 small mb-3" role="status">
                      <strong>Cancellation requested</strong>
                      <span className="d-block">
                        Customer asked to cancel on{' '}
                        {new Date(detail.cancel_requested_at).toLocaleString('en-IN')}. Review and set status to
                        cancelled if you approve.
                      </span>
                    </div>
                  ) : null}

                  <AdminOrderReturnsSection
                    sessionToken={sessionToken}
                    orderNumber={detail.order_number}
                    detail={detail}
                    onOrderRefresh={applyOrderDetail}
                    setStatus={setStatus}
                  />

                  <section className="admin-order-fulfillment">
                    <h4 className="admin-orders-section-title">Ship &amp; print</h4>
                    <div
                      className={`admin-order-fulfillment__ship-from${
                        canPrintShipDocs ? '' : ' admin-order-fulfillment__ship-from--disabled'
                      }`}
                    >
                      <label className="form-label small mb-1" htmlFor="order-ship-from">
                        Ship from
                      </label>
                      <AdminShipFromLocationSelect
                        id="order-ship-from"
                        value={shipFromId}
                        options={printOptions}
                        disabled={!canPrintShipDocs}
                        title={`Ship from ${printOptions.find((o) => o.id === shipFromId)?.label || 'location'}`}
                        aria-label="Ship from location"
                        onChange={(e) => {
                          const id = e.target.value;
                          setShipFromId(id);
                          saveShipFromId(id);
                        }}
                      />
                      {!canPrintShipDocs ? (
                        <p className="small text-muted mb-0 mt-1">
                          Available when order status is <strong>Shipped</strong>.
                        </p>
                      ) : null}
                    </div>
                    <div className="admin-order-doc-grid">
                      <button
                        type="button"
                        className={`admin-order-doc-card${canPrintShipDocs ? '' : ' admin-order-doc-card--disabled'}`}
                        disabled={!canPrintShipDocs}
                        title={
                          canPrintShipDocs
                            ? 'Items and totals for the box'
                            : 'Set status to Shipped first'
                        }
                        onClick={() => printOrderPackingSlip(detail)}
                      >
                        <AdminOrderIcon name="slip" className="admin-order-doc-card__icon" />
                        <span className="admin-order-doc-card__title">Packing slip</span>
                        <span className="admin-order-doc-card__hint">
                          {canPrintShipDocs ? 'Items & totals for the box' : 'Shipped orders only'}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`admin-order-doc-card admin-order-doc-card--primary${
                          canPrintShipDocs ? '' : ' admin-order-doc-card--disabled'
                        }`}
                        disabled={!canPrintShipDocs}
                        title={
                          canPrintShipDocs
                            ? 'Parcel sticker for courier'
                            : 'Set status to Shipped first'
                        }
                        onClick={() =>
                          printShippingLabel(
                            detail,
                            senderForId(printOptions, shipFromId),
                            shipFromConfig.label_size
                          )
                        }
                      >
                        <AdminOrderIcon name="label" className="admin-order-doc-card__icon" />
                        <span className="admin-order-doc-card__title">Shipping label</span>
                        <span className="admin-order-doc-card__hint">
                          {canPrintShipDocs ? 'Parcel sticker for courier' : 'Shipped orders only'}
                        </span>
                      </button>
                      <button type="button" className="admin-order-doc-card" onClick={copyAddress}>
                        <AdminOrderIcon name="copy" className="admin-order-doc-card__icon" />
                        <span className="admin-order-doc-card__title">Copy delivery address</span>
                        <span className="admin-order-doc-card__hint">Paste into WhatsApp or courier app</span>
                      </button>
                    </div>
                    <p className="admin-order-fulfillment__tip small text-muted mb-0">
                      Labels and slips unlock when status is <strong>Shipped</strong>. Batch print: tick shipped
                      orders in the list, then <strong>Print labels</strong> or <strong>Print slips</strong>.
                    </p>
                  </section>

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <div className="admin-order-info-card">
                        <h4 className="admin-orders-section-title">Customer account</h4>
                        <p className="mb-1 fw-semibold">{detail.customer?.display_name || '—'}</p>
                        <p className="small text-muted mb-1">@{detail.customer?.username}</p>
                        {detail.customer?.email ? (
                          <p className="small mb-2">
                            <a href={`mailto:${detail.customer.email}`}>{detail.customer.email}</a>
                          </p>
                        ) : (
                          <p className="small text-warning mb-2">No email on account — cannot send from shop.</p>
                        )}
                        {detail.customer?.phone ? (
                          <p className="small text-muted mb-2">{detail.customer.phone}</p>
                        ) : null}
                        <div className="admin-order-email-resend">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm w-100 d-inline-flex align-items-center justify-content-center gap-2"
                            disabled={!detail.customer?.email || sendingCustomerEmail}
                            onClick={sendCustomerEmail}
                          >
                            <AdminOrderIcon name="mail" />
                            {sendingCustomerEmail ? 'Sending…' : 'Resend order email'}
                          </button>
                          <p className="small text-muted mb-0 mt-2">{customerEmailHint}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="admin-order-info-card admin-order-info-card--address">
                        <h4 className="admin-orders-section-title">Customer delivery address</h4>
                        <address className="admin-order-deliver-address mb-0">
                          <strong className="d-block">{detail.shipping?.name}</strong>
                          {detail.shipping?.phone ? <span className="d-block">{detail.shipping.phone}</span> : null}
                          {detail.shipping?.address ? <span className="d-block">{detail.shipping.address}</span> : null}
                          <span className="d-block">
                            {[detail.shipping?.city, detail.shipping?.state].filter(Boolean).join(', ')}
                            {detail.shipping?.pincode ? ` — ${detail.shipping.pincode}` : ''}
                          </span>
                        </address>
                      </div>
                    </div>
                  </div>

                  <h4 className="admin-orders-section-title">Items</h4>
                  <div className="table-responsive mb-3 admin-orders-items-table">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          {detail.partial_shipments_enabled ? <th className="admin-orders-ship-pick-col">Ship now</th> : null}
                          <th>Product</th>
                          <th>Personalization</th>
                          <th>ID</th>
                          <th className="text-end">Qty</th>
                          {detail.partial_shipments_enabled ? (
                            <th className="text-end">Fulfilled</th>
                          ) : null}
                          <th className="text-end">Price</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayItems.map((it, idx) => {
                          const remaining = detail.partial_shipments_enabled
                            ? (it.quantity_remaining ?? it.quantity)
                            : 0;
                          const shipped = it.quantity_shipped ?? 0;
                          const itemId = Number(it.id) || 0;
                          const itemKey = itemId > 0 ? String(itemId) : '';
                          const picked = itemKey ? shipmentPick[itemKey] : undefined;
                          return (
                          <tr key={`${it.product_external_id}-${idx}`}>
                            {detail.partial_shipments_enabled ? (
                              <td className="admin-orders-ship-pick-col">
                                {remaining > 0 && itemKey ? (
                                  <div className="d-flex flex-column gap-1">
                                    <label className="form-check form-check-sm mb-0 admin-orders-ship-pick">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={picked != null}
                                        onChange={(e) => {
                                          const on = e.target.checked;
                                          setShipmentPick((prev) => {
                                            const next = { ...prev };
                                            if (on) next[itemKey] = remaining;
                                            else delete next[itemKey];
                                            return next;
                                          });
                                        }}
                                      />
                                      <span className="form-check-label small">Include</span>
                                    </label>
                                    {picked != null && remaining > 1 ? (
                                      <input
                                        type="number"
                                        min={1}
                                        max={remaining}
                                        className="form-control form-control-sm"
                                        style={{ width: '4rem' }}
                                        value={picked}
                                        onChange={(e) => {
                                          const v = Math.min(
                                            remaining,
                                            Math.max(1, parseInt(e.target.value, 10) || 1)
                                          );
                                          setShipmentPick((prev) => ({ ...prev, [itemKey]: v }));
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                ) : remaining > 0 && !itemKey ? (
                                  <span className="small text-warning" title="Reload order after migration">
                                    —
                                  </span>
                                ) : (
                                  <span className="small text-success">Done</span>
                                )}
                              </td>
                            ) : null}
                            <td>{it.product_name}</td>
                            <td className="small text-muted">
                              {it.customization_text ? it.customization_text : '—'}
                            </td>
                            <td>
                              <code className="small">{it.product_external_id}</code>
                            </td>
                            <td className="text-end">{it.quantity}</td>
                            {detail.partial_shipments_enabled ? (
                              <td className="text-end small">
                                {shipped} / {it.quantity}
                              </td>
                            ) : null}
                            <td className="text-end">{formatMoney(it.unit_price)}</td>
                            <td className="text-end">{formatMoney(it.line_total)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {detail.partial_shipments_enabled ? (
                    <section className="admin-orders-partial-shipments mb-3">
                      <h4 className="admin-orders-section-title">Partial shipments</h4>
                      <p className="small text-muted mb-2">
                        Send one item now and the other later — each package gets its own AWB and customer email.
                      </p>
                      {(detail.shipments || []).length > 0 ? (
                        <ul className="list-unstyled admin-orders-shipment-history mb-3">
                          {(detail.shipments || []).map((s) => (
                            <li key={s.id || s.shipment_seq} className="admin-orders-shipment-history__item">
                              <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
                                <strong className="small">Shipment {s.shipment_seq}</strong>
                                <span className="small text-muted">{s.shipped_at || ''}</span>
                              </div>
                              <ul className="small mb-1 ps-3">
                                {(s.items || []).map((si, i) => (
                                  <li key={`${si.order_item_id}-${i}`}>
                                    {si.product_name} × {si.quantity}
                                  </li>
                                ))}
                              </ul>
                              {s.tracking_number || s.tracking_url ? (
                                <p className="small mb-1">
                                  {s.tracking_carrier_name ? `${s.tracking_carrier_name} · ` : ''}
                                  {s.tracking_number ? (
                                    <code>{s.tracking_number}</code>
                                  ) : null}
                                  {s.tracking_url ? (
                                    <>
                                      {' '}
                                      <a href={s.tracking_url} target="_blank" rel="noopener noreferrer">
                                        Track
                                      </a>
                                    </>
                                  ) : null}
                                </p>
                              ) : (
                                <p className="small text-warning mb-1">No tracking saved for this shipment.</p>
                              )}
                              <p className="small text-muted mb-1">
                                {s.notification_sent_at
                                  ? `Customer emailed ${s.notification_sent_at}`
                                  : 'Customer email not sent for this shipment'}
                              </p>
                              {detail.customer?.email ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  disabled={resendingShipmentSeq === s.shipment_seq}
                                  onClick={() => resendShipmentEmail(s.shipment_seq)}
                                >
                                  {resendingShipmentSeq === s.shipment_seq ? 'Sending…' : 'Resend parcel email'}
                                </button>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : detail.status === 'partially_shipped' ? (
                        <p className="small text-warning mb-3">
                          Status is “partially shipped” but no shipment is on file. Use{' '}
                          <strong>Record shipment &amp; notify customer</strong> below (do not only change status).
                        </p>
                      ) : null}
                      {canRecordPartialShipment ? (
                        <div className="admin-orders-partial-shipments__actions">
                          <p className="small mb-2">
                            Tick items above, enter tracking below, then click{' '}
                            <strong>Record shipment</strong> — the Fulfilled column updates only after that
                            (not when you tick Include).
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={savingShipment || Object.keys(shipmentPick).length === 0}
                            onClick={recordShipment}
                          >
                            {savingShipment ? 'Recording…' : 'Record shipment & notify customer'}
                          </button>
                        </div>
                      ) : partialShipmentBlockReason ? (
                        <p className="small text-warning mb-0">{partialShipmentBlockReason}</p>
                      ) : (
                        <p className="small text-success mb-0">All items have been shipped.</p>
                      )}
                    </section>
                  ) : null}

                  <section className="admin-orders-tracking">
                    <h4 className="admin-orders-section-title">
                      {detail.partial_shipments_enabled && orderHasRemainingItems
                        ? 'Tracking for next parcel'
                        : 'Shipment tracking'}
                    </h4>
                    {detail.partial_shipments_enabled && orderHasRemainingItems ? (
                      <p className="small text-muted mb-2">
                        Past parcels are listed above. Enter tracking here only for the next shipment.
                      </p>
                    ) : null}
                    <p className="small text-muted mb-2">{shippedNotificationHint}</p>
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-sm-6 col-lg-4">
                        <label className="form-label small mb-1" htmlFor="order-tracking-carrier">
                          Courier
                        </label>
                        <select
                          id="order-tracking-carrier"
                          className="form-select form-select-sm"
                          value={trackingCarrier}
                          onChange={(e) => {
                            setTrackingCarrier(e.target.value);
                            trackingUrlTouchedRef.current = false;
                          }}
                        >
                          <option value="">— Select courier —</option>
                          {shippingPartners.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4">
                        <label className="form-label small mb-1" htmlFor="order-tracking-num">
                          Tracking / AWB
                        </label>
                        <input
                          id="order-tracking-num"
                          type="text"
                          className="form-control form-control-sm"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          placeholder="AWB / courier ref"
                        />
                      </div>
                      <div className="col-12 col-lg-4">
                        <label className="form-label small mb-1" htmlFor="order-tracking-url">
                          Tracking link
                        </label>
                        <input
                          id="order-tracking-url"
                          type="url"
                          className="form-control form-control-sm"
                          value={trackingUrl}
                          onChange={(e) => {
                            setTrackingUrl(e.target.value);
                            trackingUrlTouchedRef.current = true;
                          }}
                          placeholder="Auto from courier template"
                        />
                      </div>
                    </div>
                    <div className="mt-3 pt-1">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm px-3"
                        onClick={saveTracking}
                        disabled={savingTracking}
                      >
                        {savingTracking ? 'Saving…' : 'Save tracking'}
                      </button>
                    </div>
                  </section>

                  <section className="admin-orders-status-bar">
                    <h4 className="admin-orders-section-title">Fulfillment status</h4>
                    <div className="d-flex flex-wrap align-items-end gap-2">
                    <div>
                      <label className="form-label small mb-1 visually-hidden" htmlFor="order-status">
                        Fulfillment status
                      </label>
                      <select
                        id="order-status"
                        className="form-select form-select-sm"
                        value={statusDraft}
                        onChange={(e) => setStatusDraft(e.target.value)}
                        style={{ minWidth: '10rem' }}
                      >
                        {statusDraft === 'partially_shipped' ? (
                          <option value="partially_shipped">Partially shipped (set by shipments)</option>
                        ) : null}
                        {STATUS_EDIT_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    {detail.status === 'partially_shipped' ? (
                      <span className="badge text-bg-info align-self-center">Partially shipped</span>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={saveStatus}
                      disabled={savingStatus}
                    >
                      {savingStatus ? 'Saving…' : 'Update status'}
                    </button>
                    {canMarkPaid ? (
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        onClick={markPaid}
                        disabled={markingPaid}
                      >
                        {markingPaid ? '…' : 'Mark as paid'}
                      </button>
                    ) : null}
                    </div>
                  </section>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
